import { css } from "@emotion/react";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";

import { AlertIcon, colors } from "@dotnet/telematics-design-system";

import { svgFontSize } from "shared";

import Label from "../Label";
import { TOOLTIP_PLACEMENT, TooltipLarge } from "../Tooltips";
import { useInputFieldStyles } from "./hooks";
import { InputFieldProps } from "./types";

const InputField = ({
    ref,
    size,
    labelLeft,
    labelIcon,
    labelRight,
    iconLeft,
    iconRight,
    readOnly,
    isLoading,
    tooltipTitle,
    disableInteractiveTooltip,
    errorText,
    customStyle,
    inputCustomStyle,
    ...props
}: InputFieldProps) => {
    const { gray500, gray700, gray900, red700 } = colors;

    const { muiOutlinedInputStyles, circularProgressSize } =
        useInputFieldStyles();

    return (
        <div
            css={css({
                display: "inline-flex",
                flexDirection: "column",
                width: props.fullWidth ? "100%" : "unset",
                ...customStyle
            })}
        >
            {(labelLeft || labelRight) && (
                <Label
                    titleLeft={labelLeft}
                    titleRight={labelRight}
                    titleTooltip={labelIcon}
                    isRequired={props.required}
                />
            )}

            <TooltipLarge
                title={tooltipTitle || ""}
                disableInteractive={disableInteractiveTooltip}
                placement={TOOLTIP_PLACEMENT.Top}
            >
                <TextField
                    {...props}
                    ref={ref}
                    error={Boolean(errorText)}
                    autoComplete={props.autoComplete || "off"}
                    helperText={
                        errorText ? (
                            <>
                                <AlertIcon
                                    css={css({
                                        color: red700,
                                        margin: "2px 4px 0 0",
                                        alignSelf: "flex-start",
                                        ...svgFontSize(16)
                                    })}
                                />

                                {errorText}
                            </>
                        ) : (
                            ""
                        )
                    }
                    slotProps={{
                        input: {
                            ...props.slotProps?.input,
                            spellCheck: "false",
                            readOnly,
                            ...(iconLeft && {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        {iconLeft}
                                    </InputAdornment>
                                )
                            }),
                            ...((iconRight || isLoading) && {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {isLoading && (
                                            <div
                                                css={css({
                                                    display: "flex",
                                                    width: "28px",
                                                    height: "28px",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    marginRight: iconRight
                                                        ? "8px"
                                                        : "0"
                                                })}
                                            >
                                                <CircularProgress
                                                    size={circularProgressSize(
                                                        size
                                                    )}
                                                    sx={{ color: gray500 }}
                                                />
                                            </div>
                                        )}

                                        {iconRight || null}
                                    </InputAdornment>
                                )
                            }),
                            sx: [
                                {
                                    color: gray900,
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    lineHeight: "20px",
                                    letterSpacing: "0.1px",

                                    ...muiOutlinedInputStyles(
                                        size,
                                        props.disabled,
                                        readOnly
                                    )
                                },
                                ...(inputCustomStyle
                                    ? [{ ...inputCustomStyle }]
                                    : [])
                            ]
                        }
                    }}
                    sx={{
                        "& .MuiFormHelperText-root": {
                            margin: "6px 0 0",
                            display: "inline-flex",
                            alignItems: "center",
                            color: gray700,
                            fontSize: "14px",
                            lineHeight: "20px",
                            fontWeight: "500",
                            letterSpacing: "0.1px",

                            "&.Mui-error": {
                                color: gray700
                            }
                        },

                        "& .MuiInputAdornment-root": {
                            height: "0px"
                        }
                    }}
                />
            </TooltipLarge>
        </div>
    );
};

export default InputField;
